import React from "react";
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as actions from '../stores/actionsList';
import Configurer from "../components/Configurer";
import StagesManager from "../components/StagesManager";
import { checkPermission, checkTranslation, pathMaker, translate } from "../utils/useful";
import PageStatusViewer from "../components/PageStatusViewer";
import AdminFormApplet from "./admin/AdminFormApplet";
import { getPagesForUserRoute } from "../variables/userPages";
import { fakeFormsData } from "../variables/fakeData";
import { isLocalRolePreviewSession } from "../utils/localRolePreview";
import {
    createLocalPreviewUserPage,
    getLocalPreviewUserRouteRecords,
    getLocalPreviewUserTypeForRoute,
} from "../utils/localUserRoutePreview";

class UserApplet extends React.Component {
    state = {
        pages: [
            { name: 'Dashboard', key: '', dontShowInDashboard: true },
        ],
        page: null,
        path: '',
        pageStatus: 200,
        oldPath: null,
        pageType: null
    }

    componentDidMount() {
        this.props.setHeader({ 
            title: this.getPageTitle(), 
            baseUrl: this.props.baseUrl 
        })
        setTimeout(() => {
            this.checkDefaultTab()
        }, 50);
    }

    componentDidUpdate(prevProps) {
        if (this.state.oldPath !== this.props.location.pathname) {
            this.setState({ oldPath: this.props.location.pathname }, () => {
                this.checkDefaultTab()
            })
        }
    }

    getPageTitle() {
        const pathname = this.props.location.pathname
        if (pathname.includes('/operations')) {
            return translate('My Operations')
        } else if (pathname.includes('/orders')) {
            return translate('My Orders')
        }
        return translate('User Dashboard')
    }

    getPageKey() {
        const pathname = this.props.location.pathname
        if (pathname.includes('/operations')) {
            return 'operations'
        } else if (pathname.includes('/orders')) {
            return 'orders'
        }
        return ''
    }

    checkDefaultTab() {
        let def = ''
        let pageType = null
        let pageInfo = null
        
        let pathname = window.location.pathname.split('/')
        const pageKey = this.getPageKey()
        const userPages = getPagesForUserRoute(pageKey)
        
        // Get the page after /operations or /orders
        pathname.forEach((path, index) => {
            if (path === pageKey && pathname[index + 1]) {
                def = pathname[index + 1]
            }
        })

        let pageIsValid = false

        // Look for the page in userPages (fetchUrl: getUserRecords, self for vet/horseBreeder)
        Object.values(userPages).forEach(page => {
            if (page.key.toLowerCase() === def?.toLowerCase()) {
                pageIsValid = true
                pageType = page.pageType
                pageInfo = page
            }
        })

        // If no specific page, show dashboard or default to forms
        if (!def || def === '') {
            def = 'forms' // Default to forms page
            pageIsValid = true
            pageInfo = userPages.forms
            pageType = userPages.forms?.pageType
        }

        if (pageIsValid && pageInfo) {
            this.setState({ 
                oldPath: this.props.location.pathname, 
                pageStatus: 200 
            }, () => {
                this.setState({ path: def }, () => {
                    setTimeout(() => {
                        this.setState({ page: null }, () => {
                            this.setState({ page: pageInfo })
                        })
                    }, 100)
                })
            })
        } else {
            this.setState({ pageStatus: 404 })
        }
    }

    changePage = (page) => {
        const pageKey = this.getPageKey()
        let path = pathMaker(`/${pageKey}/${page}`)
        this.props.history.push(path)
    }

    setUser = (data) => {
        this.props.actions.setUser(data, null, true)
    }

    addNotif = (options) => {
        this.props.actions.addNotif(options)
    }

    informParent = (message) => {
        if (message === 'permissionDenied') {
            this.setState({ pageStatus: 401 })
        }
        if (message === 'notFound') {
            this.setState({ pageStatus: 404 })
        }
    }

    render() {
        const userId = this.props.user?.info?._id
        const pageKey = this.getPageKey()
        const localPreviewUserType = getLocalPreviewUserTypeForRoute(pageKey)
        const isLocalPreview = Boolean(localPreviewUserType)
            && isLocalRolePreviewSession(this.props.user?.info)
        const previewRecords = isLocalPreview
            ? getLocalPreviewUserRouteRecords({
                records: fakeFormsData,
                route: pageKey,
                userId,
            })
            : null
        
        // Keep the real page object stable. AdminFormApplet uses page changes to
        // decide when to fetch, so cloning it on every render causes a request loop.
        // Ownership is enforced by the backend from the authenticated session.
        let modifiedPage = this.state.page
        
        if (isLocalPreview && modifiedPage) {
            modifiedPage = createLocalPreviewUserPage(modifiedPage)
        }

        let variables = {
            permissionRoot: 'user.' + pageKey,
            userId: userId
        }

        return (
            <Configurer
                settingsInfo={{
                    showFooter: true, 
                    showTabBar: true, 
                    showHeader: true, 
                    headerTitle: this.getPageTitle(), 
                    button: {}
                }}
                title={this.getPageTitle()}
                description={translate('View and manage your ' + (pageKey === 'operations' ? 'operations' : 'orders'))}
                className="p-0 min-full-height w-100"
            >
                <PageStatusViewer status={this.state.pageStatus}>
                    <div className="w-100">
                        <div className="row m-0">
                            <div className="col-12 p-0">
                                <StagesManager ref={el => this.stageManager = el} fast={true}>
                                    <AdminFormApplet 
                                        pages={this.state.pages} 
                                        page={modifiedPage}
                                        permissionBaseKey={'user'} 
                                        baseUrl={this.props.baseUrl} 
                                        stage={1} 
                                        windowData={this.props.windowData} 
                                        user={this.props.user} 
                                        setUser={this.setUser} 
                                        addNotif={this.addNotif} 
                                        informParent={this.informParent} 
                                        history={this.props.history} 
                                        variables={variables} 
                                        stageName={'form'} 
                                        route={pageKey}
                                        localPreview={isLocalPreview}
                                        previewRecords={previewRecords}
                                        viewerUserType={isLocalPreview
                                            ? localPreviewUserType
                                            : this.props.user?.info?.userType}
                                    />
                                </StagesManager>
                            </div>
                        </div>
                    </div>
                </PageStatusViewer>
            </Configurer>
        )
    }
}

const mapStateToProps = state => ({ settings: state.settings, user: state.user })
const mapDispatchToProps = dispatch => ({ actions: bindActionCreators(actions, dispatch) })

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(UserApplet);

